'use client';

import ForBrokersIcon from '@icons/ForBrokers.svg';
import TPAsImage from '@images/use-cases/TpasImage.webp';
import { Containers, Widgets } from '@components';
import { CompanySubnav } from '@/components/elements/CompanySubnav';
import FullyCompliantIcon from '@icons/FullyCompliant.svg';
import ScaleWithoutComplexityIcon from '@icons/ScaleComplexity.svg';
import {
    TPAS_USE_CASES_WHY_HOW_BLOCKS,
    TPAS_USE_CASES_WHY_HOW_SECTION_TITLE,
} from '@/utils/content/tpasWhyHow';

export default function TpasPage() {
    return (
        <>
            <CompanySubnav section="use-cases" currentLabel="TPAs" />
            <Containers.Section
                el={
                    <Widgets.UseCasesHero
                        badge={{
                            iconSrc: ForBrokersIcon,
                            label: 'Built for TPAs',
                        }}
                        title="TPAs - Third Party Administrators"
                        description="For a Pre-tax Administrator (handling FSAs, HSAs, and HRAs), adding an ICHRA solution often feels like a natural extension, but it usually introduces a massive technical hurdle: Individual Plan Selection. Traditional pre-tax admins are built to process claims and move money, not to manage an insurance shopping mall. By white-labeling Predictable Benefits, you transform from a 'commodity vendor' into a 'strategic platform partner.'"
                        image={{
                            src: TPAsImage,
                            alt: 'Four business professionals collaborating in a modern office lounge.',
                            priority: true,
                            objectPosition: '32% center',
                        }}
                        
                        overlayCardsPositionClassName="bottom-[65%] right-3 md:bottom-[70%] md:right-4 lg:bottom-3 lg:right-3 lg:-translate-x-80"
                        overlayCards={[
                            {
                                title: 'Stay Fully Compliant',
                                subtitle:
                                    'Handle regulations with built-in workflows',
                                iconSrc: FullyCompliantIcon,
                                className: 'w-full max-w-[320px] shrink-0',
                            },
                            {
                                title: 'Scale Without Complexity',
                                subtitle: 'Manage multiple groups from one platform',
                                iconSrc: ScaleWithoutComplexityIcon,
                                className: 'w-full max-w-[290px] shrink-0',
                            },
                        ]}
                    />
                }
            />
            <Containers.Section el={<Widgets.HeroOvalDivider color="brand-50" />} />
            <Containers.Section
                el={
                    <Widgets.UseCasesWhyHow
                        sectionTitle={TPAS_USE_CASES_WHY_HOW_SECTION_TITLE}
                        blocks={TPAS_USE_CASES_WHY_HOW_BLOCKS}
                        pillNavMode="fromBlocks"
                    />
                }
            />
            <Containers.Section
                el={
                    <Widgets.ThePivotScript
                        eyebrow="SOLUTION"
                        title='The "Total Account Control" Pivot Script'
                        description='Scenario: A Pre-tax Admin is talking to a Broker who is worried about a client moving to a "Full-Turnkey" ICHRA startup that also offers HSAs.'
                        cards={[
                            {
                                title: 'The Strategy',
                                description: 'Admin: "We know the "New-Age" ICHRA platforms are flashy, but they treat the actual pre-tax compliance—the IRS reporting, the HIPAA-secure reimbursements, and the HSA integration—as an afterthought. We’ve been the masters of tax-advantaged accounts for decades."',
                            },
                            {
                                title: 'The Tech Upgrade',
                                description: 'Admin: "That’s why we’ve added the [Admin Name] ICHRA . We’ve kept our gold-standard compliance engine and added a world-class shopping front-end. Now, when your employees log in to check their HSA balance, they can also shop for their ICHRA medical plan in the same branded environment."',
                            },
                            {
                                title: 'The Seamless Experience',
                                description: 'Admin: "The employer gets one invoice. We handle the premium routing, the tax-reporting, and the enrollment. You don\'t have to manage three different vendors; everything is under our roof."',
                            },
                            {
                                title: 'The Closing Hook',
                                description: 'Admin: "Why risk moving your client\'s HSA and FSA to a startup that\'s never handled a complex audit? Let’s keep the core benefits with us and just turn on the ICHRA shopping module for this group. I can have a demo site ready for you by tomorrow afternoon."',
                            },
                        ]}
                    />
                }
            />
            <Containers.Section
                el={
                    <Widgets.StrategicComparison
                        title="Strategic Comparison"
                    />
                }
            />
            <Containers.Section
                el={
                    <Widgets.TheDefense />
                }
            />
            <Containers.Section
                el={<Widgets.ExploreOtherUseCases excludeSlug="tpas" />}
            />
            <Containers.Section el={<Widgets.ReadToBuild />} />
        </>
    );
}
